import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import team01 from '../../../assets/images/team-01-david-amore.jpg';
import team02 from '../../../assets/images/team-02-agbaakin-solade.jpg';
import team03 from '../../../assets/images/team-03-bukola-rose.jpg';
import team04 from '../../../assets/images/team-04-kenneth-okoh.jpg';
import team05 from '../../../assets/images/team-05-kunle-adejumo.jpg';
import team06 from '../../../assets/images/team-06-hannah-adenike.jpg';
import team07 from '../../../assets/images/team-07-israel-adelani.jpg';
import team08 from '../../../assets/images/team-08-deborah-blessing.jpg';
import team09 from '../../../assets/images/team-09-amb-raph.jpg';
import team10 from '../../../assets/images/team-10-maria-airaoje.jpg';

const TeamContainer = styled.section`
  position: relative;
  padding: 120px 0;
  background: linear-gradient(180deg, #000000 0%, #1a1a1a 50%, #000000 100%);
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled(motion.h2)`
  font-family: 'Cinzel', serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  background: linear-gradient(45deg, #FFD700, #FFA500);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  margin-bottom: 2rem;
  letter-spacing: 2px;
`;

const SectionSubtitle = styled(motion.p)`
  font-family: 'Inter', sans-serif;
  font-size: 1.2rem;
  color: #CCCCCC;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 4rem;
  line-height: 1.6;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2rem;
  margin-top: 60px;
`;

const TeamCard = styled(motion.div)`
  position: relative;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.05) 0%, rgba(0, 0, 0, 0.8) 100%);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.1), transparent);
    transition: left 0.8s ease;
    z-index: 1;
    pointer-events: none;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-10px);
    border-color: rgba(255, 215, 0, 0.5);
    box-shadow: 0 20px 40px rgba(255, 215, 0, 0.1);
  }
`;

const TeamPhotoWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1024 / 1280;
  background: linear-gradient(45deg, #333, #555);
  overflow: hidden;
`;

const TeamPhoto = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: opacity 0.3s ease, transform 0.6s ease;

  &.loading {
    opacity: 0.5;
  }

  &.loaded {
    opacity: 1;
  }

  ${TeamCard}:hover & {
    transform: scale(1.03);
  }
`;

const PhotoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(45deg, #333, #555);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFD700;
  font-size: 2rem;
  position: absolute;
  top: 0;
  left: 0;
`;

// Lazy loading image component
const LazyImage = ({ src, alt, className }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageInView, setImageInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setImageInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <div ref={imgRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      {!imageLoaded && (
        <PhotoPlaceholder>
          👤
        </PhotoPlaceholder>
      )}
      {imageInView && (
        <TeamPhoto
          src={src}
          alt={alt}
          className={imageLoaded ? 'loaded' : 'loading'}
          onLoad={handleImageLoad}
          loading="lazy"
        />
      )}
    </div>
  );
};

const TeamSection = () => {
  const teamMembers = [
    { name: 'David S. Amore', photo: team01 },
    { name: 'Agbaakin O. Solade', photo: team02 },
    { name: 'Bukola Rose', photo: team03 },
    { name: 'Kenneth Okoh', photo: team04 },
    { name: 'Kunle Adejumo', photo: team05 },
    { name: 'Hannah Adenike', photo: team06 },
    { name: 'Israel Adelani', photo: team07 },
    { name: 'Deborah Blessing', photo: team08 },
    { name: 'Amb. Raph', photo: team09 },
    { name: 'Maria Airaoje', photo: team10 }
  ];

  return (
    <TeamContainer className="about-section">
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Meet Our Team
        </SectionTitle>

        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          The creative minds and industry experts behind every Brose Films production, bringing decades of experience and passion to every project.
        </SectionSubtitle>

        <TeamGrid>
          {teamMembers.map((member, index) => (
            <TeamCard
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <TeamPhotoWrap>
                <LazyImage src={member.photo} alt={member.name} />
              </TeamPhotoWrap>
            </TeamCard>
          ))}
        </TeamGrid>
      </Container>
    </TeamContainer>
  );
};

export default TeamSection;
